import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ArrowLeft, FileText, Save, RefreshCw, Eye, Edit, Plane, Bus, MapPin, Globe, AlertCircle, X } from 'lucide-react';
import { contractsAPI } from '../../services/api';

const AdminContracts = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const categoryIcons = {
    'nacional-aereo': <Plane className="w-5 h-5" />,
    'internacional-aereo': <Globe className="w-5 h-5" />,
    'passeio-1-dia': <MapPin className="w-5 h-5" />,
    'rodoviario': <Bus className="w-5 h-5" />
  };

  const categoryColors = {
    'nacional-aereo': 'bg-blue-600',
    'internacional-aereo': 'bg-purple-600',
    'passeio-1-dia': 'bg-green-600',
    'rodoviario': 'bg-orange-600'
  };

  const categoryNames = {
    'nacional-aereo': 'Nacional Aéreo',
    'internacional-aereo': 'Internacional',
    'passeio-1-dia': 'Passeio de 1 Dia',
    'rodoviario': 'Rodoviário'
  };

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    loadTemplates();
  }, [navigate]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await contractsAPI.getTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      setMessage({ type: 'error', text: 'Erro ao carregar templates de contrato' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (template) => {
    try {
      setSaving(true);
      await contractsAPI.updateTemplate(template.id, {
        content: template.content,
        name: template.name
      });
      setMessage({ type: 'success', text: 'Contrato salvo com sucesso!' });
      setEditingTemplate(null);
      loadTemplates();
    } catch (error) {
      console.error('Erro ao salvar:', error);
      setMessage({ type: 'error', text: 'Erro ao salvar contrato' });
    } finally {
      setSaving(false);
    }
  };

  const handleEditContent = (templateId, newContent) => {
    setTemplates(prev => prev.map(t => 
      t.id === templateId ? { ...t, content: newContent } : t
    ));
  };

  const renderPreview = (content) => {
    let preview = content;
    preview = preview.replace(/\{\{NOME_COMPLETO\}\}/g, 'João da Silva Santos');
    preview = preview.replace(/\{\{CPF\}\}/g, '123.456.789-00');
    preview = preview.replace(/\{\{DATA_NASCIMENTO\}\}/g, '15/03/1990');
    preview = preview.replace(/\{\{PACOTE\}\}/g, 'Chapada dos Veadeiros');
    preview = preview.replace(/\{\{DATA_VIAGEM\}\}/g, '25/01/2025 a 30/01/2025');
    preview = preview.replace(/\{\{VALOR_TOTAL\}\}/g, 'R$ 2.500,00');
    preview = preview.replace(/\{\{DATA_ATUAL\}\}/g, new Date().toLocaleString('pt-BR'));
    return preview;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/admin')} className="flex items-center space-x-2 hover:opacity-80">
              <ArrowLeft className="w-5 h-5" />
              <span>Voltar</span>
            </button>
            <div className="h-6 w-px bg-white/30"></div>
            <div className="flex items-center space-x-2">
              <FileText className="w-6 h-6" />
              <h1 className="text-xl font-bold">Contratos</h1>
            </div>
          </div>
          <Button onClick={loadTemplates} variant="outline" size="sm" className="text-white border-white hover:bg-white hover:text-[#003366]">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center justify-between ${
            message.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {message.text}
            </div>
            <button onClick={() => setMessage({ type: '', text: '' })}><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Placeholders Info */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <h3 className="font-semibold text-gray-900 mb-2">📝 Placeholders Disponíveis</h3>
            <p className="text-sm text-gray-600 mb-2">
              Use os seguintes placeholders no contrato (serão substituídos automaticamente pelos dados do cliente):
            </p>
            <div className="flex flex-wrap gap-2">
              {['{{NOME_COMPLETO}}', '{{CPF}}', '{{DATA_NASCIMENTO}}', '{{PACOTE}}', '{{DATA_VIAGEM}}', '{{VALOR_TOTAL}}', '{{DATA_ATUAL}}'].map(p => (
                <code key={p} className="bg-amber-100 px-2 py-1 rounded text-xs text-amber-900 font-mono">{p}</code>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Contracts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {templates.map((template) => (
            <Card key={template.id} className="overflow-hidden">
              <div className={`${categoryColors[template.category]} text-white p-4`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      {categoryIcons[template.category]}
                    </div>
                    <div>
                      <h3 className="font-bold">{template.name}</h3>
                      <p className="text-sm opacity-80">{categoryNames[template.category]}</p>
                    </div>
                  </div>
                </div>
              </div>
              <CardContent className="p-4">
                <div className="flex gap-2 mb-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPreviewTemplate(previewTemplate === template.id ? null : template.id)}
                    className="flex-1"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    {previewTemplate === template.id ? 'Fechar' : 'Preview'}
                  </Button>
                  <Button
                    size="sm"
                    variant={editingTemplate === template.id ? 'default' : 'outline'}
                    onClick={() => setEditingTemplate(editingTemplate === template.id ? null : template.id)}
                    className="flex-1"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    {editingTemplate === template.id ? 'Cancelar' : 'Editar'}
                  </Button>
                </div>

                {editingTemplate === template.id ? (
                  <div className="space-y-4">
                    <div>
                      <Label>Conteúdo do Contrato (HTML)</Label>
                      <Textarea
                        value={template.content}
                        onChange={(e) => handleEditContent(template.id, e.target.value)}
                        className="mt-1 font-mono text-xs min-h-[300px]"
                      />
                    </div>
                    <Button
                      onClick={() => handleSave(template)}
                      disabled={saving}
                      className="w-full bg-[#003366] hover:bg-[#002244]"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Salvando...' : 'Salvar Alterações'}
                    </Button>
                  </div>
                ) : previewTemplate === template.id ? (
                  <div className="border rounded-lg p-4 bg-white max-h-[400px] overflow-y-auto">
                    <div 
                      className="prose prose-sm max-w-none contract-preview"
                      dangerouslySetInnerHTML={{ __html: renderPreview(template.content) }}
                    />
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    Este contrato será exibido para clientes que comprarem pacotes da categoria <strong>{categoryNames[template.category]}</strong>.
                    {template.updated_at && (
                      <span className="block text-xs text-gray-400 mt-2">
                        Última atualização: {new Date(template.updated_at).toLocaleString('pt-BR')}
                      </span>
                    )}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <style>{`
        .contract-preview h1 { font-size: 1.1rem; font-weight: bold; text-align: center; margin-bottom: 0.25rem; }
        .contract-preview h2 { font-size: 0.9rem; text-align: center; color: #666; margin-bottom: 1rem; }
        .contract-preview h3 { font-size: 0.85rem; font-weight: bold; margin-top: 1rem; margin-bottom: 0.25rem; color: #003366; }
        .contract-preview .customer-info { background: #f5f5f5; padding: 0.75rem; border-radius: 0.5rem; margin: 0.5rem 0; }
        .contract-preview ul { margin-left: 1.25rem; list-style-type: disc; font-size: 0.85rem; }
        .contract-preview li { margin-bottom: 0.15rem; }
        .contract-preview p { font-size: 0.85rem; margin-bottom: 0.25rem; }
      `}</style>
    </div>
  );
};

export default AdminContracts;
